import React, { useState, useEffect } from "react";
import OtpInput from "react-otp-input";
import { generatePath, Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Form, Row, Col } from "react-bootstrap";
import email_icon from "../../../Assets/svg/login/gmail.svg";
import Toast from "react-hot-toast";
import { post } from "../../../helpers/api_helper";
import { ROUTES } from "../..";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { type } = useParams();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const qEmail = decodeURI(searchParams.get("email") || "");

  const [email, setEmail] = useState(qEmail);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const [timer, setTimer] = useState(15);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [showTryAgain, setShowTryAgain] = useState(false);

  useEffect(() => {
    if (isTimerActive && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else if (timer === 0) {
      setIsTimerActive(false);
      setTimer(15);
    }
  }, [isTimerActive, timer]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await post("/user/forgot/password", { email: email });
      if (response?.success) {
        Toast.success("OTP sent to your email!");
        setOtpSent(true);
        setIsTimerActive(true);
        setTimer(15);
      }
    } catch (error) {
      console.error("Error sending OTP", error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let values = { email: email, otp: otp };
      const response = await post("/user/verify/otp", values);
      if (response?.success) {
        Toast.success(response?.message);
        navigate("/reset-password", { state: { email, otp, type } });
      }
    } catch (error) {
      console.error("Error verifying OTP", error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCount < 3) {
      setLoading(true);
      try {
        const response = await post("/user/forgot/password", { email: email });
        if (response?.success) {
          Toast.success("OTP resent!");
          setResendCount((prev) => prev + 1);
          setIsTimerActive(true);
          if (resendCount === 0) {
            setTimer(15);
          } else {
            setTimer(30);
          }
        }
      } catch (error) {
        console.error("Failed to resend OTP", error);
      } finally {
        setLoading(false);
      }
    } else {
      setShowTryAgain(true);
      Toast.error("Maximum resend limit reached.");
    }
  };

  const handleTryAgain = () => {
    setShowTryAgain(false);
    setResendCount(0);
    setOtpSent(false);
    setEmail("");
    navigate(generatePath(ROUTES.FORGOT_PASSWORD));
  };

  return (
    <div className="formInner">
      <div className="top py-2 text-center">
        <h2 className="m-0 fw-sbold head">Forgot Password!</h2>
        <p className="m-0 py-1">Please enter your details</p>
      </div>
      <Form onSubmit={handleSendOtp}>
        <Row>
          {!otpSent && (
            <>
              <Col lg="12" className="my-3">
                <div className="FormHeading">
                  <h4 className="m-0 fw-sbold">Email Id Details</h4>
                  <p className="m-0">
                    Please enter your email address to request a password reset
                  </p>
                </div>
              </Col>
              <Col lg="12" className="my-3">
                <div className="position-relative labelInput">
                  <div className="iconWithText position-relative">
                    <input
                      type="email"
                      className="form-control"
                      placeholder="abc@gmail.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                    <span className="icn position-absolute" style={{ left: "8px" }}>
                      <img src={email_icon} alt="email icon" />
                    </span>
                  </div>
                  <label className="form-label position-absolute d-inline-block bg-white m-0 px-1">
                    Email
                  </label>
                </div>
              </Col>
              <Col lg="12" className="my-3">
                <Button
                  type="submit"
                  className="d-flex align-items-center justify-content-center w-100 commonBtn"
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Send OTP"}
                </Button>
              </Col>
            </>
          )}
          {otpSent && (
            <>
              <Col lg="12" className="my-3">
                <div className="FormHeading">
                  <h4 className="m-0 fw-sbold themeClr">Verification Code</h4>
                  <p className="m-0">
                    Please type the verification code sent to{" "}
                    <span className="themeClr">{email}</span>
                  </p>
                </div>
              </Col>
              <Col lg="12" className="my-3">
                <div className="otp mb-3">
                  <OtpInput
                    value={otp}
                    onChange={setOtp}
                    numInputs={4}
                    renderInput={(props) => <input {...props} />}
                  />
                </div>
                <p className="text-muted fw-sbold m-0">
                  I don’t receive a code!{" "}
                  {showTryAgain ? (
                    <span
                      className="themeClr fw-sbold"
                      role="button"
                      onClick={handleTryAgain}
                    >
                      Try again
                    </span>
                  ) : (
                    <Button
                      className="p-0 border-0 themeClr fw-sbold"
                      variant="transparent"
                      onClick={handleResendOtp}
                      disabled={loading || isTimerActive}
                      style={{
                        color: isTimerActive ? "lightgray" : "blue",
                        pointerEvents: isTimerActive ? "none" : "auto",
                      }}
                    >
                      {isTimerActive
                        ? `Please Resend in ${timer}s`
                        : "Please Resend"}
                    </Button>
                  )}
                </p>
              </Col>
              <Col lg="12" className="my-3">
                <Button
                  onClick={handleVerifyOtp}
                  className="d-flex align-items-center justify-content-center w-100 commonBtn"
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Done"}
                </Button>
              </Col>
            </>
          )}
          <Col lg="12" className="my-3">
            <div className="text-center mt-3">
              <p className="m-0 fw-sbold">
                Don&apos;t have an account?{" "}
                <Link className="themeClr" to={"/signup"}>
                  Sign Up
                </Link>
              </p>
            </div>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default ForgotPassword;
